import { ReactNode, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Sidebar as PrimeSidebar } from "primereact/sidebar";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { ScannerDialog } from "../../features/scanner/ScannerDialog";
import { ProfileDialog } from "./ProfileDialog";
import Science from "@mui/icons-material/Science";

type NavIcon = string | ReactNode;

interface NavItem {
    to: string;
    label: string;
    icon: NavIcon;
    // Uma permissão, ou uma lista (basta ter QUALQUER uma delas).
    permission?: string | string[];
    operator?: boolean;
}
interface NavGroup {
    label: string;
    icon: string;
    items: NavItem[];
}

const dashboardItem: NavItem = { to: "/", label: "Visão geral", icon: "pi-home", operator: true };
const guideItem: NavItem = { to: "/guide", label: "Guia do usuário", icon: "pi-book" };

const navGroups: NavGroup[] = [
    {
        label: "Operações",
        icon: "pi-sync",
        items: [
            {
                to: "/picking",
                label: "Separação",
                icon: "pi-list-check",
                permission: ["picking.operate", "fulfillment.view"],
                operator: true,
            },
            { to: "/receipts", label: "Recebimentos", icon: "pi-truck", permission: "receiving.manage" },
            { to: "/requests", label: "Requisições", icon: "pi-send", permission: ["requests.manage", "fulfillment.view"] },
            { to: "/production", label: "Produção", icon: "pi-cog", permission: "production.manage", operator: true },
            { to: "/inventory", label: "Inventário", icon: "pi-clipboard", permission: "inventory.count", operator: true },
            { to: "/quality", label: "Qualidade", icon: "pi-verified", permission: "quality.change_status" },
        ],
    },
    {
        label: "Estoque",
        icon: "pi-box",
        items: [
            { to: "/stock", label: "Estoque", icon: "pi-box", operator: true },
            { to: "/stock-by-lot", label: "Saldo por lote", icon: "pi-table", permission: "stock.view" },
            { to: "/movements", label: "Movimentações", icon: "pi-arrows-h", permission: "ledger.read" },
            { to: "/reorg", label: "Reorganização", icon: "pi-sort-amount-down", permission: "stock.view" },
            { to: "/merge-pallets", label: "Unificar paletes", icon: "pi-clone", permission: "handling_units.manage" },
            { to: "/manual-consume", label: "Consumo manual", icon: "pi-minus-circle", permission: "consumption.manual" },
        ],
    },
    {
        label: "Cadastros",
        icon: "pi-folder",
        items: [
            { to: "/products", label: "Produtos", icon: "pi-tags", permission: "products.manage" },
            { to: "/formulas", label: "Fórmulas", icon: <Science />, permission: "formulas.manage" },
            { to: "/suppliers", label: "Fornecedores", icon: "pi-building", permission: "suppliers.manage" },
            { to: "/warehouse", label: "Estrutura do armazém", icon: "pi-sitemap", permission: "warehouse.manage" },
        ],
    },
    {
        label: "Administração",
        icon: "pi-shield",
        items: [
            { to: "/users", label: "Usuários", icon: "pi-users", permission: "users.view" },
            { to: "/roles", label: "Papéis", icon: "pi-shield", permission: "roles.manage" },
            { to: "/audit", label: "Auditoria", icon: "pi-history", permission: "audit.read" },
        ],
    },
];

const mobileNav: NavItem[] = [dashboardItem, ...navGroups.flatMap((group) => group.items)];

const initials = (name?: string) =>
    (name || "OP")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0])
        .join("")
        .toUpperCase();

const renderIcon = (icon: NavIcon) => {
    if (typeof icon === "string") return <i className={`pi ${icon}`} />;
    return <span className="sidebar-icon sidebar-svg-icon">{icon}</span>;
};

// Mostra o item quando não exige permissão ou o usuário tem QUALQUER uma das exigidas.
const canSeeNav = (perm: string | string[] | undefined, has: (key: string) => boolean) =>
    !perm || (Array.isArray(perm) ? perm.some((key) => has(key)) : has(perm));

function Navigation({ onNavigate }: { onNavigate?: () => void }) {
    const { hasPermission } = useAuth();
    const { pathname } = useLocation();

    const visibleGroups = navGroups
        .map((group) => ({
            ...group,
            items: group.items.filter((item) => canSeeNav(item.permission, hasPermission)),
        }))
        .filter((group) => group.items.length > 0);

    const [open, setOpen] = useState<Record<string, boolean>>(() =>
        Object.fromEntries(
            visibleGroups.map((group) => [
                group.label,
                group.items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`)),
            ]),
        ),
    );

    const toggle = (label: string) => setOpen((state) => ({ ...state, [label]: !state[label] }));

    return (
        <nav className="main-nav">
            <NavLink to={dashboardItem.to} end onClick={onNavigate}>
                {renderIcon(dashboardItem.icon)}
                <span>{dashboardItem.label}</span>
            </NavLink>
            <NavLink to={guideItem.to} onClick={onNavigate}>
                {renderIcon(guideItem.icon)}
                <span>{guideItem.label}</span>
            </NavLink>
            {visibleGroups.map((group) => {
                const isOpen = open[group.label] ?? false;
                return (
                    <div key={group.label} className={`nav-group${isOpen ? " nav-group--open" : ""}`}>
                        <button
                            type="button"
                            className="nav-group-header"
                            aria-expanded={isOpen}
                            onClick={() => toggle(group.label)}
                        >
                            <i className={`pi ${group.icon}`} />
                            <span>{group.label}</span>
                            <i className="pi pi-chevron-down nav-group-caret" />
                        </button>
                        {isOpen && (
                            <div className="nav-group-items">
                                {group.items.map((item) => (
                                    <NavLink key={item.to} to={item.to} onClick={onNavigate}>
                                        {renderIcon(item.icon)}
                                        <span>{item.label}</span>
                                    </NavLink>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </nav>
    );
}

export function AppShell() {
    const [menuVisible, setMenuVisible] = useState(false);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const { user, logout, hasPermission } = useAuth();
    const { warehouses, warehouseId, setWarehouseId } = useWorkspace();
    const mobileItems = mobileNav
        .filter((item) => item.operator && canSeeNav(item.permission, hasPermission))
        .slice(0, 4);
    const sidebarContent = (
        <>
            <div className="brand-mark">
                <img src="/logos/coopervass.png" alt="Coopervass" className="brand-logo brand-logo--sidebar" />
            </div>
            <Navigation onNavigate={() => setMenuVisible(false)} />
            <div className="sidebar-user">
                <button
                    type="button"
                    className="sidebar-user__info"
                    aria-label="Editar meus dados"
                    title="Editar meus dados"
                    onClick={() => {
                        setProfileOpen(true);
                        setMenuVisible(false);
                    }}
                >
                    <span className="avatar">{initials(user?.name)}</span>
                    <div>
                        <strong>{user?.name}</strong>
                        <small />
                    </div>
                </button>
                <Button icon="pi pi-sign-out" text rounded aria-label="Sair" onClick={logout} />
            </div>
        </>
    );
    return (
        <div className="app-shell">
            <aside className="desktop-sidebar">{sidebarContent}</aside>
            <PrimeSidebar visible={menuVisible} onHide={() => setMenuVisible(false)} className="mobile-drawer">
                {sidebarContent}
            </PrimeSidebar>
            <ScannerDialog visible={scannerOpen} onHide={() => setScannerOpen(false)} />
            <ProfileDialog visible={profileOpen} onHide={() => setProfileOpen(false)} />
            <div className="app-column">
                <header className="topbar">
                    <Button
                        className="menu-trigger"
                        icon="pi pi-bars"
                        text
                        rounded
                        aria-label="Abrir menu"
                        onClick={() => setMenuVisible(true)}
                    />
                    <div className="topbar-brand">
                        <img src="/logos/logo-coopervass.png" alt="" aria-hidden="true" />
                        <strong>WMS</strong>
                    </div>
                    <Dropdown
                        value={warehouseId}
                        options={warehouses}
                        optionLabel="name"
                        optionValue="id"
                        placeholder="Armazém"
                        className="warehouse-switcher"
                        onChange={(event) => setWarehouseId(event.value as number)}
                    />
                    <Button
                        icon="pi pi-qrcode"
                        rounded
                        text
                        aria-label="Leitor de QR"
                        tooltip="Leitor de QR (palete ou endereço)"
                        tooltipOptions={{ position: "bottom" }}
                        onClick={() => setScannerOpen(true)}
                    />
                </header>
                <main className="app-content">
                    <Outlet />
                </main>
                <nav className="bottom-nav">
                    {mobileItems.map((item) => (
                        <NavLink key={item.to} to={item.to} end={item.to === "/"}>
                            <i className={`pi ${item.icon}`} />
                            <span>{item.label.split(" ")[0]}</span>
                        </NavLink>
                    ))}
                    <button type="button" onClick={() => setMenuVisible(true)}>
                        <i className="pi pi-ellipsis-h" />
                        <span>Mais</span>
                    </button>
                </nav>
            </div>
        </div>
    );
}
